import { createBrowserRouter } from "react-router-dom";
import {
  MassiveMusicHorizontal,
  Responsive,
} from "@songtradr-one/components-frontend";
import {
  AppHeader,
  AppHeaderMobile,
  PortalShell,
} from "@songtradr-one/navigation-frontend";
import { AppId, routes } from "@songtradr-one/shared-common";
import {
  AppLayout,
  AuthRedirectRoute,
  ErrorElement,
  INVITE_LAYOUT_ID,
  InviteLayout,
  PUBLIC_LAYOUT_ID,
  PublicLayout,
  SignOutForwarderRoute,
  USER_LAYOUT_ID,
  UserLayout,
  errorText,
} from "@songtradr-one/shared-frontend";
import { config } from "../config/config";
import { env } from "../env/env";

export const router = createBrowserRouter(
  [
    {
      ...AppLayout({
        appId: AppId.SongtradrStudio,
        appName: "Massive Music Studio",
        appNavLogo: <MassiveMusicHorizontal height="1rem" />,
        production: env.NODE_ENV === "production",
        coreBackendUrl:
          env.NX_PUBLIC_REMOTE_BASE_HOST &&
          env.NODE_ENV !== "production" &&
          window.location.hostname.includes(env.NX_PUBLIC_REMOTE_BASE_HOST)
            ? `${window.location.protocol}//api-${window.location.hostname}`
            : config.coreBackendUrl,
        ssoFrontendUrl: config.ssoFrontendUrl,
        assetsUrl: config.assetsUrl,
        helpCenterUrl: config.helpCenterUrl,
        analytics: { heap: { enabled: false, id: config.heapId } },
      }),
      errorElement: <ErrorElement errorText={errorText} />,
      children: [
        InviteLayout,
        UserLayout({
          header: (
            <Responsive mobile={<AppHeaderMobile />} desktop={<AppHeader />} />
          ),
        }),
        PublicLayout,
      ],
    },
  ],
  {
    async patchRoutesOnNavigation({ path, patch }) {
      if (path.startsWith(routes.portal.base)) {
        patch(USER_LAYOUT_ID, PortalShell);
      }

      if (path.startsWith(routes.statements.base)) {
        const module = await import(
          "@songtradr-one/statements-reports-frontend"
        );
        patch(USER_LAYOUT_ID, module.StatementsShell);
      }

      if (path.startsWith(routes.music.base)) {
        const module = await import("@songtradr-one/music-frontend");
        patch(USER_LAYOUT_ID, module.MusicShell);
      }

      if (path.startsWith(routes.music.shared.base)) {
        const module = await import("@songtradr-one/music-frontend");
        patch(PUBLIC_LAYOUT_ID, module.MusicSharedShell);
      }

      if (path.startsWith(routes.resources.base)) {
        const module = await import("@songtradr-one/resources-frontend");
        patch(USER_LAYOUT_ID, module.ResourcesShell);
      }

      if (path.startsWith(routes.payouts.base)) {
        const module = await import("@songtradr-one/payouts-frontend");
        patch(USER_LAYOUT_ID, module.PayoutsShell);
      }

      if (path.startsWith(routes.socialClearances.base)) {
        const module = await import("@songtradr-one/social-clearance-frontend");
        patch(USER_LAYOUT_ID, module.SocialClearancesShell);
      }

      if (path.startsWith(routes.labs.base)) {
        const module = await import("@songtradr-one/labs-frontend");
        patch(USER_LAYOUT_ID, module.LabsShell);
      }

      if (
        path.startsWith(routes.orgs.base) &&
        !path.startsWith(routes.orgs.inviteAccept.replace(":id", ""))
      ) {
        const module = await import("@songtradr-one/orgs-frontend");
        patch(USER_LAYOUT_ID, module.OrgsShell);
      }

      if (path.startsWith(routes.orgs.inviteAccept.replace(":id", ""))) {
        const module = await import("@songtradr-one/orgs-frontend");
        patch(INVITE_LAYOUT_ID, module.OrgsInviteShell);
      }

      if (path.startsWith(routes.signing.base)) {
        const module = await import("@songtradr-one/signing-service-frontend");
        patch(PUBLIC_LAYOUT_ID, module.SigningServiceShell);
      }

      if (path.startsWith(routes.msa.base)) {
        const module = await import(
          "@songtradr-one/master-service-agreement-frontend"
        );
        patch(PUBLIC_LAYOUT_ID, module.MsaShell);
      }

      if (path.startsWith(routes.licenses.base)) {
        const module = await import("@songtradr-one/licenses-frontend");
        patch(USER_LAYOUT_ID, module.LicensesShell);
      }

      if (path.startsWith(routes.legal.base)) {
        const module = await import("@songtradr-one/legal-frontend");
        patch(USER_LAYOUT_ID, module.LegalShell);
      }

      if (path.startsWith(routes.reporting.base)) {
        const module = await import("@songtradr-one/reporting-frontend");
        patch(USER_LAYOUT_ID, module.ReportingShell);
      }

      if (path === routes.signout.base) {
        patch(PUBLIC_LAYOUT_ID, SignOutForwarderRoute);
      }

      if (path === routes.authRedirect.base) {
        patch(PUBLIC_LAYOUT_ID, AuthRedirectRoute);
      }
    },
  },
);
